import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserResponse } from '@ydistri/identity-sdk';
import {
  getItem,
  getSelectedTemplateForProject,
  LSKey,
  setItem,
  setSelectedTemplateForProject,
} from './localStore';
import { API, urlProjectShortName, urlSelectedTemplateId } from '../swagger/collections';
import { getSelectedProjectShortName, setSelectedProjectApiContext } from '../apis/apiHeader';
import * as Sentry from '@sentry/react';
import { INVALID_TEMPLATE_ID } from '../components/global/_constants';

export interface UserState {
  user: UserResponse | undefined;
  selectedProjectShortName: string | undefined;
  selectedTemplateId: number | undefined;
  isDeletingTemplate: number | undefined;
}

const initialState: UserState = {
  user: getItem(LSKey.USER),
  selectedProjectShortName: urlProjectShortName,
  // Only the URL and the per-project map may decide the template: a single stored template id would
  // be shared by every tab, so a tab could open another tab's template (see [LSKey] retired id '6')
  selectedTemplateId: urlSelectedTemplateId ?? getSelectedTemplateForProject(urlProjectShortName),
  isDeletingTemplate: undefined,
};

export const userSlice = createSlice({
  name: 'userReducer',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserResponse | undefined>) => {
      setItem(LSKey.USER, action.payload);
      state.user = action.payload;
      if (action.payload === undefined) {
        Sentry.setUser(null);
      } else {
        Sentry.setUser({
          id: action.payload.id,
          name: action.payload.fullName,
        });
      }
    },
    setSelectedProjectShortName: (state, action: PayloadAction<string | undefined>) => {
      // Set together, so no render sees a selected project the Project-Slug header does not name yet
      if (action.payload) {
        setSelectedProjectApiContext(API, action.payload);
      } else {
        setItem(LSKey.SELECTED_PROJECT_SHORT_NAME, undefined);
      }
      state.selectedProjectShortName = action.payload;
    },
    setSelectedTemplateId: (state, action: PayloadAction<number | undefined>) => {
      const projectShortName = getSelectedProjectShortName();
      if (projectShortName && action.payload) {
        setSelectedTemplateForProject(projectShortName, action.payload);
      }

      state.selectedTemplateId = action.payload;
    },
    clearSelectedTemplateId: state => {
      const projectShortName = getSelectedProjectShortName();
      if (projectShortName) {
        setSelectedTemplateForProject(projectShortName, INVALID_TEMPLATE_ID);
      }

      state.selectedTemplateId = INVALID_TEMPLATE_ID;
    },
    setIsDeletingTemplate: (state, action: PayloadAction<number | undefined>) => {
      state.isDeletingTemplate = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- no need to do anything but must fulfill the contract
    resetState: () => {},
  },
});

export const {
  setUser,
  setSelectedProjectShortName,
  setSelectedTemplateId,
  setIsDeletingTemplate,
  clearSelectedTemplateId,
  resetState,
} = userSlice.actions;

export const userReducer = userSlice.reducer;
